import type { ILocale, ISiteLocalization, ISiteLogo, ISiteMetadata, ISiteNavigation } from '@anywayseo/tools';
import { getLocale, getLocales, getNavigation, getSiteName } from '@anywayseo/tools';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { localNavigation } from 'content/local/data';

const GET_SITE_QUERY = graphql`
  query GetSite {
    site {
      siteMetadata {
        title
        description
        siteUrl
        currency
        locale
        locales {
          code
          name
        }
      }
    }
    file(sourceInstanceName: { eq: "images" }, relativePath: { eq: "logo.png" }) {
      publicURL
      childImageSharp {
        gatsbyImageData(height: 60)
        original {
          width
          height
        }
      }
    }
    strapiSite {
      name
      locale
      currency
      seo {
        metaTitle
        metaDescription
      }
      logo {
        alternativeText
        width
        height
        localFile {
          publicURL
          childImageSharp {
            gatsbyImageData(height: 60)
            original {
              width
              height
            }
          }
        }
      }
      socialNetworks {
        items {
          kind
          url
        }
      }
    }
    strapiNavigation {
      items {
        slug
        locale
        items {
          type
          title
          slug
          path
          items {
            type
            title
            slug
            path
          }
        }
      }
    }
    strapiLocalization {
      locales {
        code
        name
      }
    }
  }
`;

interface IUseSiteReturn {
  logo: ISiteLogo;
  metadata: ISiteMetadata;
  navigation: ISiteNavigation;
  localization: ISiteLocalization;
}

function useSite(): IUseSiteReturn {
  const { site, file, strapiSite, strapiNavigation, strapiLocalization } =
    useStaticQuery<Queries.GetSiteQuery>(GET_SITE_QUERY);

  let name;
  let title;
  let description;
  let defaultLocaleCode;
  let locales;
  let currency;
  let socialNetworks;
  let logoImage;
  let logoUrl;
  let logoAlt;
  let logoWidth;
  let logoHeight;

  if (process.env.GATSBY_SITE_SOURCE === 'local') {
    name = getSiteName(site?.siteMetadata?.siteUrl);
    title = site?.siteMetadata?.title;
    description = site?.siteMetadata?.description;
    defaultLocaleCode = site?.siteMetadata?.locale;
    locales = site?.siteMetadata?.locales;
    currency = site?.siteMetadata?.currency;
    logoImage = file?.childImageSharp?.gatsbyImageData;
    logoUrl = file?.publicURL;
    logoWidth = file?.childImageSharp?.original?.width;
    logoHeight = file?.childImageSharp?.original?.height;
  } else {
    name = strapiSite?.name;
    title = strapiSite?.seo?.metaTitle;
    description = strapiSite?.seo?.metaDescription;
    defaultLocaleCode = strapiSite?.locale;
    locales = strapiLocalization?.locales;
    currency = strapiSite?.currency;
    socialNetworks = strapiSite?.socialNetworks?.items;
    logoImage = strapiSite?.logo?.localFile?.childImageSharp?.gatsbyImageData;
    logoUrl = strapiSite?.logo?.localFile?.publicURL;
    logoAlt = strapiSite?.logo?.alternativeText;
    logoWidth = strapiSite?.logo?.width || strapiSite?.logo?.localFile?.childImageSharp?.original?.width;
    logoHeight = strapiSite?.logo?.height || strapiSite?.logo?.localFile?.childImageSharp?.original?.height;
  }

  const defaultLocale = getLocale(defaultLocaleCode as ILocale['code'], locales as ILocale[]);
  const logoSrc = logoImage ? (
    <GatsbyImage image={logoImage} alt='Logo' objectFit='contain' style={{ display: 'flex', height: '100%' }} />
  ) : (
    ''
  );

  return {
    logo: {
      src: logoSrc,
      url: logoUrl ?? '',
      alt: logoAlt ?? name ?? '',
      width: logoWidth ?? 0,
      height: logoHeight ?? 0,
    },
    metadata: {
      name: name ?? '',
      seo: {
        title: title ?? '',
        description: description ?? '',
      },
      socialNetworks: socialNetworks as ISiteMetadata['socialNetworks'],
    },
    localization: {
      currency: (currency ?? 'USD') as ISiteLocalization['currency'],
      defaultLocale,
      locales: getLocales(defaultLocale.code, locales as ILocale[]),
    },
    navigation: getNavigation(localNavigation, strapiNavigation),
  };
}

export default useSite;
